import React, { useEffect, useRef } from "react";

const COLORS = ["#b45309", "#d97706", "#92400e", "#a16207"];
const REPEL_RADIUS = 110;
const REPEL_STRENGTH = 46;
const EASE = 0.12;

export default function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width, height, dpr;
    let dots = [];
    let rafId;
    const mouse = { x: -9999, y: -9999 };

    const seed = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(500, Math.floor((width * height) / 3800));
      dots = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        ox: 0,
        oy: 0,
        r: 0.8 + Math.random() * 2.4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        baseAlpha: 0.35 + Math.random() * 0.45,
        vx: (Math.random() - 0.5) * 0.06,
        vy: (Math.random() - 0.5) * 0.06,
        twinkleSpeed: 0.4 + Math.random() * 0.8,
        twinklePhase: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (time) => {
      ctx.clearRect(0, 0, width, height);
      dots.forEach((d) => {
        if (!reduceMotion) {
          d.x += d.vx;
          d.y += d.vy;
          if (d.x < -5) d.x = width + 5;
          if (d.x > width + 5) d.x = -5;
          if (d.y < -5) d.y = height + 5;
          if (d.y > height + 5) d.y = -5;

          const dx = d.x + d.ox - mouse.x;
          const dy = d.y + d.oy - mouse.y;
          const dist = Math.hypot(dx, dy);

          let targetOx = 0;
          let targetOy = 0;
          if (dist < REPEL_RADIUS && dist > 0.01) {
            const push = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
            targetOx = (dx / dist) * push;
            targetOy = (dy / dist) * push;
          }
          d.ox += (targetOx - d.ox) * EASE;
          d.oy += (targetOy - d.oy) * EASE;
        }

        const twinkle = reduceMotion
          ? 1
          : 0.7 + 0.3 * Math.sin(time * 0.001 * d.twinkleSpeed + d.twinklePhase);
        ctx.beginPath();
        ctx.arc(d.x + d.ox, d.y + d.oy, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = d.baseAlpha * twinkle;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      if (!reduceMotion) rafId = requestAnimationFrame(draw);
    };

    seed();
    draw(0);

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const handleMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", handleMouseMove);
    document.documentElement.addEventListener("mouseleave", handleMouseLeave);

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        seed();
        if (reduceMotion) draw(0);
      }, 200);
    };
    window.addEventListener("resize", handleResize);

    const handleVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafId);
      } else if (!reduceMotion) {
        rafId = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimeout);
      window.removeEventListener("mousemove", handleMouseMove);
      document.documentElement.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-10 pointer-events-none"
      aria-hidden="true"
    />
  );
}
